import React, { useMemo, useRef, useState, useCallback } from 'react';
import Slider from 'react-slick';
import {
  CarouselContainer,
  ArrowContainer,
  PaginationCounter,
  VehicleImage,
} from './ImageGallery.style';
import { ImagesParamsMap, ImageParamsMappingType } from './config';
import { GalleryImagesType } from '../../types';
import ArrowLeftIcon from '../../assets/icons/ArrowLeftIcon';
import ArrowRightIcon from '../../assets/icons/ArrowRightIcon';
import { ONDEMAND_CAROUSEL_GALLERY } from '../../constants/environment';

interface ImageGalleryProps {
  count: number;
  images: GalleryImagesType;
  isNewVehicle: boolean;
  isTier3Page?: boolean;
  isReservedVehicle: boolean | undefined;
  alt: string;
  bodyType: string | null;
  hasDealerImages?: boolean;
}

const ImagesGallery: React.FC<ImageGalleryProps> = ({
  count,
  images,
  isNewVehicle,
  isReservedVehicle,
  alt,
  bodyType,
  isTier3Page,
  hasDealerImages,
}) => {
  const [galleryIndex, setIndex] = useState<number>(0);
  const sliderRef = useRef<Slider>(null);
  const [loadedImages, setLoadedImages] = useState<number[]>([]); // Keep track of loaded images

  const PrevArrow = useMemo(() => {
    return (
      <ArrowContainer type="button" data-role="none" aria-label="Previous arrow">
        <ArrowLeftIcon />
      </ArrowContainer>
    );
  }, []);

  const NextArrow = useMemo(
    () => (
      <ArrowContainer type="button" data-role="none" aria-label="Next arrow">
        <ArrowRightIcon />
      </ArrowContainer>
    ),
    [],
  );

  const handleIndex = useCallback(
    (nextIndex: number) => {
      if (nextIndex === galleryIndex) return;
      setIndex(nextIndex);
    },
    [galleryIndex],
  );

  // Change the slide using the left and right arrows on keyboard when “focus” is brought to the gallery
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      sliderRef.current?.slickPrev();
    } else if (event.key === 'ArrowRight') {
      sliderRef.current?.slickNext();
    }
  };

  const handleImageLoad = (index: number[]) => {
    // Update the list of loaded images
    setLoadedImages((prevLoadedImages) => [...prevLoadedImages, index[0]]);
  };

  return (
    <CarouselContainer
      isNew={isNewVehicle}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      isTier3={isTier3Page}
      hasDealerImages={hasDealerImages}
    >
      <Slider
        infinite
        speed={1000}
        slidesToShow={1}
        slidesToScroll={1}
        nextArrow={NextArrow}
        prevArrow={PrevArrow}
        onLazyLoad={(slideIndex) => handleImageLoad(slideIndex)}
        beforeChange={(currentSlide, nextSlide) => {
          handleIndex(nextSlide);
        }}
        accessibility
        arrows={!(count < 2)} // Showing no arrows when there is only one image or no image
        lazyLoad={ONDEMAND_CAROUSEL_GALLERY ? 'ondemand' : 'progressive'} // The other option that library provides is lazyLoad="ondemand"
        ref={sliderRef}
      >
        {images.map((item, index) => {
          const imageParams = ImagesParamsMap[item.type as keyof ImageParamsMappingType] || '';
          const isImageLoaded = loadedImages.includes(index);
          return (
            <VehicleImage
              src={item.url + imageParams}
              alt={isImageLoaded ? `${alt}-imageType${item.type}-${index}` : ''}
              isNewVehicle={isNewVehicle}
              isReservedVehicle={isReservedVehicle}
              key={item.url}
              imageType={item.type || ''}
              bodyType={bodyType}
              isTier3={isTier3Page}
              hasDealerImages={hasDealerImages}
            />
          );
        })}
      </Slider>
      <PaginationCounter>
        <span>{galleryIndex + 1}</span>/{count}
      </PaginationCounter>
    </CarouselContainer>
  );
};

export default ImagesGallery;
